import { Stack, Typography, useTheme } from "@mui/material";
import type { Control, FieldValues, Path } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";

type PensionBasicFieldsProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	namePlaceholder: string;
	contractNumberPlaceholder: string;
};

export const PensionBasicFields = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
	namePlaceholder,
	contractNumberPlaceholder,
}: PensionBasicFieldsProps<FormType, TContext, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(
		control,
		ClientPortfolioDomainEnum.PensionProducts,
	);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddPension.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name={"name" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.name)}
					placeholder={namePlaceholder}
				/>
				<FlatRowFormInput
					control={control}
					name={"contractNumber" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.contractNumber)}
					placeholder={contractNumberPlaceholder}
				/>
				<FlatRowFormCombobox
					control={control}
					name={"producer" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
				/>
				<FlatRowFormCombobox
					control={control}
					name={"product" as Path<FormType>}
					label={t(Resources.Portfolio.AddPension.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					isLast
				/>
			</Stack>
		</>
	);
};
